import React from 'react';
import ReactDOM from 'react-dom';
import { debounce } from 'lodash';
import i18n from '../../services/i18n';
import { createMainApplication } from './createMainApplication';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ClientIndustryService, createIconsProvider, LogoWebAppApi, LogoUrlGenerator, BngApi, DomainCheckerService } from '@wix/branding-client-lib';
import routingMiddleware from './middlewares/routingMiddleware';
import { mainStateToUrlParams } from './mainStateToUrlParams';
import * as screenActions from './actions/screenActions';
import * as actionTypes from './mainActionTypes';
import businessNameGeneratorMiddleware from './middlewares/businessNameGeneratorMiddleware';
import { BusinessNameGenerator } from '../../../universal/Domain/BusinessNameGenerator';
import { UserDetailsService } from '../../services/UserDetailsService';
import { HttpClient } from '@wix/http-client';

const mountPoint = window.__MOUNT_POINT__;
const locale = window.__LOCALE__;
const basename = window.__BASEURL__; // TODO: suppose to be base path?
const endpoints = window.__ENDPOINTS__;
const debugApp = window.__DEBUG_APP__;
const initialState = window.__INITIAL_STATE__;
const initializedState = window.__INITIALIZED_STATES__;
const environment = window.__ENVIRONMENT__ || {};
const experiments = window.__EXPERIMENTS__ || {};
// const { desktopMinWidth } = s;
const desktopMinWidth = 1000;
const { isMobile } = environment;
const desktopMinWidthInt = parseInt(desktopMinWidth);
const playgroundElement = document.createElement('div');
playgroundElement.id = 'svgplayground';
playgroundElement.style.position = 'absolute';
playgroundElement.style.visibility = 'hidden';
playgroundElement.style.top = '0px';
playgroundElement.style.left = '0px';
playgroundElement.style.overflow = 'hidden';
playgroundElement.style.hight = '0px';
playgroundElement.setAttribute('style', 'position:absolute;visibility:hidden;top:0;left:0;overflow:hidden;height:0;');

const bodyElement = document.getElementsByTagName('body')[0];
bodyElement.appendChild(playgroundElement);

const {
  /* wixmpApiDomain, wixmpFilesBaseUrl, logoProviderBaseUrl, */ logoWebappApi,
  adiContentServiceBaseUrl,
  premiumDomainRegistrarApiBase
} = endpoints;
const customHistory = createBrowserHistory({ basename: mountPoint });

// Init http clients
const domainCheckerHttpClient = new HttpClient({ baseURL: premiumDomainRegistrarApiBase });
const industryServiceHttpClient = new HttpClient({ baseURL: adiContentServiceBaseUrl.replace('https://www.wix.com', '') });
const bngApiHttpClient = new HttpClient({ baseURL: mountPoint });
const logoWebAppApiHttpClient = new HttpClient({ baseURL: logoWebappApi });
const useDetailsHttpClient = new HttpClient({ baseURL: '/_api' });

// Init services
const logoWebAppApi = new LogoWebAppApi(logoWebAppApiHttpClient);
const domainCheckerService = new DomainCheckerService(domainCheckerHttpClient);
const userDetailsService = new UserDetailsService(useDetailsHttpClient);
const industryService = new ClientIndustryService(locale, industryServiceHttpClient);
const bngApi = new BngApi(bngApiHttpClient);

const iconsProvider = createIconsProvider(logoWebAppApi, industryService, { basename });

const businessNameGenerator = new BusinessNameGenerator({
  iconsProvider,
  bngApi,
  domainCheckerService,
  userDetailsService
});

(async () => {
  const i18Instance = await i18n(locale);
  const middlewares = [];
  const logoUrlGenerator = new LogoUrlGenerator({
    protocol: location.protocol,
    host: 'www.wix.com',
    mountPoint: `/logo/maker/esh`,
    debugApp,
    isMobile
  });
  const logoWebappApiV2 = {};
  const experimentService = {};
  middlewares.push(businessNameGeneratorMiddleware(businessNameGenerator));
  middlewares.push(routingMiddleware(businessNameGenerator, customHistory, basename, logoUrlGenerator, logoWebappApiV2, experimentService));

  const { component, store } = createMainApplication(
    content => {
      return (
        <Router basename={mountPoint} history={customHistory}>
          {content}
        </Router>
      );
    },
    i18Instance,
    initialState,
    middlewares,
    experiments,
    { industryService, logoUrlGenerator },
    {
      initializedState,
      notifyDataReady: stateName => {
        const storeState = store.getState();

        store.dispatch({
          type: actionTypes.NOTIFY_INITIAL_STATE_READY,
          payload: {
            state: storeState[stateName],
            stateName
          }
        });
      },
      fetch: async fetchData => {
        const { stateName, baseState } = fetchData;
        const storeState = store.getState();
        const baselState = {};
        baseState.forEach(baseStateName => {
          baselState[baseStateName] = storeState[baseStateName];
        });
        const result = await bngApiHttpClient.post(`/api/fetchRouteInitialState/${stateName}`, baselState).then(({ data }) => data);
        store.dispatch({
          type: actionTypes.SET_FETCHED_INITIAL_STATE,
          payload: {
            state: result,
            stateName
          }
        });
      }
    }
  );

  function dispatchScreenData() {
    const type = window.innerWidth < desktopMinWidthInt ? 'mobile' : 'desktop';
    store.dispatch(screenActions.setScreenSize({ width: window.innerWidth, height: window.innerHeight, type }));
  }

  window.addEventListener(
    'resize',
    debounce(() => {
      dispatchScreenData();
    }, 200)
  );

  dispatchScreenData();

  store.subscribe(
    debounce(() => {
      const { businessIdea, tags, industry, purpose, businessDetails } = store.getState();
      const params = mainStateToUrlParams({ businessIdea, tags, industry, purpose, businessDetails }, debugApp);

      if (params) {
        customHistory.replace(`?${params}`);
      }
    }, 200)
  );

  const lottieLoader = async () => {
    // const lottieLoaderData = await import(/* webpackChunkName: "lottie" */ `../assets/lottie/lottieData_${locale}.json`);
    // store.dispatch(setLottieData({ lottieLogoListData }));
  };

  ReactDOM.render(component, document.getElementById('root'), lottieLoader);
})();
